import { useState, type RefObject, type SyntheticEvent } from "react";
import { ArrowUp, Bot, Sparkles } from "lucide-react";
import { Link } from "react-router";
import {
  chatSteps as steps,
  chatSuggestions as suggestions,
  type ChatMessage,
  type ContractorPublic,
  type ViewerMode,
} from "../agent-chat-model.js";
import type {
  IntakeScopeCandidate,
  IntakeState,
  IntakeWorkItem,
} from "../structured-intake-model.js";
import { ProjectContextSidebar, ProjectProgressSidebar } from "./AgentChatSidebars.js";
import { ChatMessageContent } from "./ChatMessageContent.js";
import { Shell } from "./Shell.js";
import { StructuredIntakeCards } from "./StructuredIntakeCards.js";
import { QuoteReviewModal, type QuoteFormSubmission } from "./QuoteReviewModal.js";

interface Props {
  acceptStandardDimension: (candidateId: string) => Promise<void>;
  classificationBusy: string | null;
  confirmClassification: (workItem: IntakeWorkItem, projectType: string) => Promise<void>;
  confirmMeasurement: (measurementId: string) => Promise<void>;
  contractor: ContractorPublic["contractor"] | null;
  contractorSlug: string;
  conversationEnd: RefObject<HTMLDivElement | null>;
  decideFact: (factId: string, decision: "confirm" | "reject") => Promise<void>;
  decideObservation: (observationId: string, decision: "confirm" | "reject") => Promise<void>;
  resolveScope: (
    candidate: IntakeScopeCandidate,
    resolution: "accept" | "reject" | "correct",
    projectType?: string,
  ) => Promise<void>;
  error: string;
  estimateBusy: boolean;
  input: string;
  intakeActionBusy: string | null;
  intakeMode: boolean;
  intakeState: IntakeState | null;
  jobOfferings: ContractorPublic["jobOfferings"];
  jobTypeCode: string;
  loading: boolean;
  messages: ChatMessage[];
  pending: boolean;
  progress: number;
  projectId: string;
  savedEstimate: { currency: string; total: { lowMinor: string; highMinor: string } } | null;
  saveQuoteForm: (facts: QuoteFormSubmission[]) => Promise<void>;
  sendMessage: (event?: SyntheticEvent<HTMLFormElement>, suggestedText?: string) => Promise<void>;
  sending: boolean;
  setInput: (value: string) => void;
  uploadCapture: (
    task: NonNullable<IntakeState["captureTasks"]>[number],
    file: File,
  ) => Promise<void>;
  viewerMode: ViewerMode;
}

export function AgentChatWorkspace(props: Props) {
  const [reviewOpen, setReviewOpen] = useState(false);
  return (
    <Shell accountMode={props.viewerMode === "contractor" ? "contractor" : "public"}>
      <main className="min-h-[calc(100vh-73px)] bg-white">
        <header className="flex min-h-16 items-center justify-between gap-4 border-b border-stone-200 px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <p className="truncate font-bold text-emerald-950">
              {props.contractor ? `Project with ${props.contractor.name}` : "New project"}
            </p>
            <p className="text-xs text-stone-500">Private draft · Nothing shared yet</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link
              className="hidden rounded-xl px-3 py-2 text-sm font-semibold text-stone-600 hover:bg-stone-100 sm:inline-flex"
              to="/chat"
            >
              Start with another contractor
            </Link>
            {props.jobTypeCode === "flooring" ? (
              <Link
                className="rounded-xl border border-stone-300 bg-white px-3 py-2 text-sm font-semibold text-stone-700 hover:border-emerald-700 hover:text-emerald-800"
                to={`/quote/${props.contractorSlug}?jobType=flooring`}
              >
                Detailed planner
              </Link>
            ) : null}
          </div>
        </header>

        <div className="mx-auto grid min-h-[calc(100vh-137px)] max-w-[1600px] xl:grid-cols-[250px_minmax(0,1fr)_310px]">
          <ProjectProgressSidebar steps={steps} progress={props.progress} />

          <section className="flex min-h-[calc(100vh-137px)] min-w-0 flex-col bg-white">
            <div className="border-b border-stone-100 px-4 py-3 xl:hidden">
              <div className="flex items-center justify-between text-xs font-semibold text-stone-500">
                <span>{steps[Math.min(props.progress, steps.length - 1)]}</span>
                <span>
                  {props.progress} of {steps.length}
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-stone-200">
                <div
                  className="h-full rounded-full bg-emerald-700 transition-all"
                  style={{ width: `${(props.progress / steps.length) * 100}%` }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-8 sm:px-6">
              <div className="mx-auto max-w-3xl">
                <div className="mb-8 text-center">
                  <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-emerald-950 text-white shadow-lg shadow-emerald-900/15">
                    <Sparkles size={22} />
                  </span>
                  <h1 className="mt-4 text-2xl font-black tracking-tight text-emerald-950">
                    Let’s plan your project
                  </h1>
                  <p className="mt-2 text-sm text-stone-500">
                    Describe it naturally. I’ll ask for the details needed to build a useful plan.
                  </p>
                </div>

                {props.loading ? (
                  <div className="flex items-center justify-center gap-2 py-12 text-sm text-stone-500">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-700" />
                    Starting your private project guide…
                  </div>
                ) : null}

                <div aria-live="polite" className="space-y-6">
                  <StructuredIntakeCards
                    classificationBusy={props.classificationBusy}
                    intakeActionBusy={props.intakeActionBusy}
                    intakeMode={props.intakeMode}
                    intakeState={props.intakeState}
                    jobOfferings={props.jobOfferings}
                    onAcceptStandardDimension={props.acceptStandardDimension}
                    onConfirmClassification={props.confirmClassification}
                    onConfirmMeasurement={props.confirmMeasurement}
                    onResolveScope={props.resolveScope}
                    onDecideFact={props.decideFact}
                    onDecideObservation={props.decideObservation}
                    onUploadCapture={props.uploadCapture}
                  />
                  {props.intakeMode ? (
                    <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                      {props.savedEstimate ? (
                        <>
                          <p className="font-bold text-emerald-950">
                            Your planning estimate is saved
                          </p>
                          <p className="mt-1 text-2xl font-black text-emerald-950">
                            {new Intl.NumberFormat("en-CA", {
                              style: "currency",
                              currency: props.savedEstimate.currency,
                            }).format(Number(props.savedEstimate.total.lowMinor) / 100)}
                            {props.savedEstimate.total.lowMinor ===
                            props.savedEstimate.total.highMinor
                              ? ""
                              : ` – ${new Intl.NumberFormat("en-CA", {
                                  style: "currency",
                                  currency: props.savedEstimate.currency,
                                }).format(Number(props.savedEstimate.total.highMinor) / 100)}`}
                          </p>
                          <p className="mt-2 text-sm text-stone-600">
                            Draft quote · Awaiting contractor approval
                          </p>
                          <Link
                            className="mt-4 inline-flex rounded-xl bg-emerald-800 px-4 py-2.5 text-sm font-bold text-white"
                            to="/login?claim=true"
                          >
                            Sign in to save & submit
                          </Link>
                        </>
                      ) : (
                        <>
                          <p className="font-bold text-emerald-950">Review your project brief</p>
                          <p className="mt-1 text-sm text-emerald-900/75">
                            We’ll use the details above and clearly label any assumptions.
                          </p>
                          <button
                            className="mt-4 rounded-xl bg-emerald-800 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                            disabled={props.estimateBusy}
                            onClick={() => setReviewOpen(true)}
                            type="button"
                          >
                            Review quote
                          </button>
                        </>
                      )}
                    </section>
                  ) : null}
                  {props.messages.map((message) =>
                    message.role === "status" ? (
                      <div className="flex justify-center" key={message.id}>
                        <p className="rounded-full bg-stone-100 px-4 py-2 text-xs font-semibold text-stone-500">
                          {message.text}
                        </p>
                      </div>
                    ) : (
                      <div
                        className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
                        key={message.id}
                      >
                        {message.role === "agent" ? (
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-800">
                            <Bot size={18} />
                          </span>
                        ) : null}
                        <div
                          className={`max-w-[85%] rounded-2xl px-4 py-3 text-[15px] leading-7 sm:max-w-[75%] ${
                            message.role === "user"
                              ? "rounded-br-md bg-emerald-900 text-white"
                              : "rounded-bl-md border border-stone-200 bg-stone-50 text-stone-700"
                          }`}
                        >
                          <ChatMessageContent
                            markdown={message.role === "agent"}
                            text={message.text}
                          />
                          {message.role === "user" && message.state === "failed" ? (
                            <button
                              className="mt-2 block text-xs font-bold text-emerald-100 underline"
                              onClick={() => void props.sendMessage(undefined, message.text)}
                              type="button"
                            >
                              Retry
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ),
                  )}
                  {props.sending || props.pending ? (
                    <div className="flex gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-800">
                        <Bot size={18} />
                      </span>
                      <div className="flex items-center gap-1 rounded-2xl rounded-bl-md border border-stone-200 bg-stone-50 px-4 py-4">
                        {[0, 1, 2].map((dot) => (
                          <span
                            className="h-1.5 w-1.5 animate-pulse rounded-full bg-stone-400"
                            key={dot}
                            style={{ animationDelay: `${dot * 140}ms` }}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div ref={props.conversationEnd} />
                </div>

                {!props.loading && props.messages.length === 1 ? (
                  <div className="mt-7 grid gap-2 sm:grid-cols-3">
                    {suggestions.map((suggestion) => (
                      <button
                        className="rounded-xl border border-stone-200 bg-white p-3 text-left text-sm leading-5 text-stone-600 transition hover:border-emerald-400 hover:bg-emerald-50"
                        key={suggestion}
                        onClick={() => void props.sendMessage(undefined, suggestion)}
                        type="button"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                ) : null}

                {props.error ? (
                  <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <p>{props.error}</p>
                    {props.projectId ? (
                      <button
                        className="mt-2 font-bold underline"
                        onClick={() => window.location.reload()}
                        type="button"
                      >
                        Retry the project guide
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <form
              className="border-t border-stone-200 bg-white px-4 py-4 sm:px-6"
              onSubmit={(event) => void props.sendMessage(event)}
            >
              <div className="mx-auto max-w-3xl">
                <div className="flex items-end gap-2 rounded-2xl border border-stone-300 bg-white p-2 shadow-[0_8px_30px_rgba(28,41,38,0.08)] focus-within:border-emerald-700 focus-within:ring-4 focus-within:ring-emerald-100">
                  <label className="sr-only" htmlFor="agent-message">
                    Message the project guide
                  </label>
                  <textarea
                    className="max-h-36 min-h-11 flex-1 resize-none bg-transparent px-3 py-2.5 text-[15px] leading-6 outline-none"
                    disabled={props.loading || Boolean(props.error && props.messages.length === 0)}
                    id="agent-message"
                    onChange={(event) => props.setInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                      }
                    }}
                    placeholder="Describe your project or answer the guide…"
                    rows={1}
                    value={props.input}
                  />
                  <button
                    aria-label="Send message"
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-emerald-800 text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={
                      !props.input.trim() || props.loading || props.sending || props.pending
                    }
                    type="submit"
                  >
                    <ArrowUp size={19} />
                  </button>
                </div>
                <p className="mt-2 text-center text-[11px] text-stone-400">
                  Mezoniq can make mistakes. Review measurements and project details before sharing.
                </p>
              </div>
            </form>
          </section>

          <ProjectContextSidebar
            contractor={props.contractor}
            contractorSlug={props.contractorSlug}
            projectId={props.projectId}
          />
        </div>
      </main>
      <QuoteReviewModal
        busy={props.estimateBusy}
        error={props.error}
        form={props.intakeState?.quoteForm}
        onClose={() => setReviewOpen(false)}
        onSubmit={async (facts) => {
          await props.saveQuoteForm(facts);
          setReviewOpen(false);
        }}
        open={reviewOpen}
      />
    </Shell>
  );
}
